import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";

import Feedback from "ds/components/Feedback";
import useTypedContext from "hooks/useTypedContext";
import { StackSettingsContextData } from "views/Stack/Settings/Context";
import FeedbackActions from "ds/components/Feedback/Actions";
import Button from "ds/components/Button";
import Link from "ds/components/Link";
import { ArrowCircle } from "components/icons/generated";
import { VENDOR_CONFIG_TYPENAME, VENDOR_KEY } from "constants/vendor";
import { OpenTofuMigrationState, StackVendor, TerraformWorkflowTool } from "types/generated";
import useTypedFlags from "hooks/useTypedFlags";
import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import { OPEN_TOFU_MIGRATION_QUEUE_PATH } from "views/Account/OpenTofuMigration/constants";

import { showStackSettingsOpenTofuMigrationConfirmationModal } from "./ConfirmationModal";
import { showStackSettingsOpenTofuRollbackConfirmationModal } from "./RollbackConfirmationModal";
import { OPEN_TOFU_MIGRATE_SEARCH, OPEN_TOFU_MIGRATION_QUEUE_SEARCH } from "./gql";

const SEARCH_INPUT_SLUG = (stackId: string) => ({
  first: 1,
  after: null,
  fullTextSearch: null,
  orderBy: null,
  requestedPage: null,
  predicates: [
    {
      field: "stackSlug",
      exclude: null,
      constraint: {
        booleanEquals: null,
        enumEquals: null,
        stringMatches: null,
        stringEquals: [stackId],
        hierarchyNodeValueEquals: null,
        hierarchyNode: null,
        timeInRange: null,
        timeInLast: null,
      },
    },
  ],
});

const SEARCH_INPUT_MIGRATE_SLUG = (stackId: string) => ({
  first: 1,
  after: null,
  fullTextSearch: null,
  orderBy: null,
  requestedPage: null,
  predicates: [
    {
      field: "slug",
      exclude: null,
      constraint: {
        booleanEquals: null,
        enumEquals: null,
        stringMatches: null,
        stringEquals: [stackId],
        hierarchyNodeValueEquals: null,
        hierarchyNode: null,
        timeInRange: null,
        timeInLast: null,
      },
    },
  ],
});

const isMigrationInProgress = (state?: OpenTofuMigrationState) =>
  state === OpenTofuMigrationState.ToMigrate || state === OpenTofuMigrationState.ToRollback;

type StackSettingsOpenTofuMigrationProps = {
  vendorKey?: VENDOR_KEY;
};

const StackSettingsOpenTofuMigration = ({ vendorKey }: StackSettingsOpenTofuMigrationProps) => {
  const { opentofuBackend } = useTypedFlags();
  const { stackSettings } = useTypedContext(StackSettingsContextData);

  const canBeMigrated = useMemo(() => {
    const vendorConfig = stackSettings.vendorConfig;
    return (
      vendorKey === VENDOR_KEY.TERRAFORM &&
      vendorConfig?.__typename === VENDOR_CONFIG_TYPENAME.TERRAFORM &&
      vendorConfig.workflowTool === TerraformWorkflowTool.OpenTofu &&
      !stackSettings.labels?.some((label) => label === "terragrunt")
    );
  }, [stackSettings.labels, stackSettings.vendorConfig, vendorKey]);

  const isOpenTofu = vendorKey === VENDOR_KEY.OPEN_TOFU;
  const shouldCheckQueue = opentofuBackend && (canBeMigrated || isOpenTofu);

  const { data: queueData } = usePolledQuery(OPEN_TOFU_MIGRATION_QUEUE_SEARCH, {
    variables: { input: SEARCH_INPUT_SLUG(stackSettings.id) },
    skip: !shouldCheckQueue,
    fetchPolicy: "network-only",
    pollingGroup: PollingIntervalGroups.SingleEntities,
    shouldPoll: (data) =>
      isMigrationInProgress(data?.openTofuMigrationQueueSearch?.edges?.[0]?.node?.state),
  });

  const { data: rollbackEligibilityData } = useQuery(OPEN_TOFU_MIGRATE_SEARCH, {
    variables: {
      input: SEARCH_INPUT_MIGRATE_SLUG(stackSettings.id),
      target: StackVendor.Terraform,
    },
    skip: !opentofuBackend || !isOpenTofu,
  });

  const queueEntry = queueData?.openTofuMigrationQueueSearch.edges[0]?.node;
  const queueState = queueEntry?.state;

  const canRollbackMigration =
    isOpenTofu && (rollbackEligibilityData?.openTofuMigrateSearch.edges.length ?? 0) > 0;

  const handleMigrateToOpenTofu = () => {
    showStackSettingsOpenTofuMigrationConfirmationModal({
      stackId: stackSettings.id,
    });
  };

  const handleRollbackToTerraform = () => {
    showStackSettingsOpenTofuRollbackConfirmationModal({
      stackId: stackSettings.id,
    });
  };

  if (!opentofuBackend) {
    return null;
  }

  if (queueState === OpenTofuMigrationState.ToMigrate) {
    return (
      <Feedback type="callout" variant="info" title="Migration in progress">
        This stack has been queued for migration to the native OpenTofu vendor. The migration will
        be processed shortly.
        <FeedbackActions>
          <Link href={OPEN_TOFU_MIGRATION_QUEUE_PATH}>View queue</Link>
        </FeedbackActions>
      </Feedback>
    );
  }

  if (queueState === OpenTofuMigrationState.ToRollback) {
    return (
      <Feedback type="callout" variant="info" title="Rollback in progress">
        This stack has been queued for rollback to the Terraform vendor. The rollback will be
        processed shortly.
        <FeedbackActions>
          <Link href={OPEN_TOFU_MIGRATION_QUEUE_PATH}>View queue</Link>
        </FeedbackActions>
      </Feedback>
    );
  }

  if (queueState === OpenTofuMigrationState.Failed) {
    return (
      <Feedback type="callout" variant="danger" title="Migration failed">
        {queueEntry?.note || "The migration failed. You can retry it."}
        <FeedbackActions>
          {canBeMigrated && (
            <Button variant="contrast" size="small" onPress={handleMigrateToOpenTofu}>
              Retry migration
            </Button>
          )}
          {isOpenTofu && canRollbackMigration && (
            <Button
              variant="secondary"
              size="small"
              startIcon={ArrowCircle}
              onPress={handleRollbackToTerraform}
            >
              Rollback to Terraform
            </Button>
          )}
          <Link href={OPEN_TOFU_MIGRATION_QUEUE_PATH}>View queue</Link>
        </FeedbackActions>
      </Feedback>
    );
  }

  if (canBeMigrated) {
    return (
      <Feedback
        type="callout"
        variant="info"
        title="Move your OpenTofu stacks to the native OpenTofu vendor"
      >
        This stack runs OpenTofu through the Terraform vendor, so it misses OpenTofu-only features.
        Migrate to get CPU profiles for slow runs, structured logs and shorter plan output now, plus
        deeper run diagnostics soon.
        <FeedbackActions>
          <Button variant="contrast" size="small" onPress={handleMigrateToOpenTofu}>
            Migrate to OpenTofu
          </Button>
        </FeedbackActions>
      </Feedback>
    );
  }

  if (canRollbackMigration) {
    return (
      <Feedback type="callout" variant="info">
        If you're having issues with OpenTofu migration, you can roll it back to Terraform.
        <FeedbackActions>
          <Button
            variant="secondary"
            size="small"
            startIcon={ArrowCircle}
            onPress={handleRollbackToTerraform}
          >
            Rollback to Terraform
          </Button>
        </FeedbackActions>
      </Feedback>
    );
  }

  return null;
};

export default StackSettingsOpenTofuMigration;
